import { api } from "./client";
import type { CreditStatus, Entitlements, InvitationInfo, TokenResponse, User } from "./types";

export async function login(username: string, password: string): Promise<void> {
  const t = await api.post<TokenResponse>("/auth/login", { json: { username, password } });
  api.setTokens(t.access_token, t.refresh_token);
}

export function signup(username: string, email: string, password: string, confirm: string, acceptTerms?: string): Promise<User> {
  return api.post<User>("/auth/signup", { json: { username, email, password, confirm_password: confirm, accept_terms: acceptTerms ?? null } });
}

/** Public: what this server allows before anyone has signed in. */
export interface AuthConfig {
  signup_enabled: boolean; email_flows_enabled: boolean;
  /** Invitations, the admin dashboard and the audit log exist on this server. */
  account_services_enabled: boolean; signup_requires_approval: boolean;
  /** Set when every person must agree to the ModelBench Terms: send it back as accept_terms. */
  terms_version?: string | null; terms_url?: string | null; privacy_url?: string | null;
}
export const authConfig = () => api.get<AuthConfig>("/auth/config");

/** Email flows (server only: the routes exist when the server has account services and email on). */
export const verifyEmail = (token: string) => api.post<void>("/auth/verify-email", { json: { token } });
export const resendVerification = (identifier: string) =>
  api.post<{ detail: string }>("/auth/resend-verification", { json: { identifier } });
export const forgotPassword = (email: string) =>
  api.post<{ detail: string }>("/auth/forgot-password", { json: { email } });
export const resetPassword = (token: string, new_password: string) =>
  api.post<void>("/auth/reset-password", { json: { token, new_password } });

export const me = () => api.get<User>("/auth/me");
/** The edition and features the caller has (backend/app/licensing/dependencies.py: entitlements). */
export const features = () => api.get<Entitlements>("/auth/features");
/** The organization's credits (backend/app/api/auth.py: my_credits). */
export const credits = () => api.get<CreditStatus>("/auth/credits");

export const changePassword = (current_password: string, new_password: string) =>
  api.post<void>("/auth/change-password", { json: { current_password, new_password } });

/** Only a local single-user install accepts this; a shared database answers 403 with the reason. */
export const recoverPassword = (username: string, new_password: string) =>
  api.post<void>("/auth/recover-password", { json: { username, new_password } });

export function logout(): void { api.clearTokens(); }

export const lookupInvitation = (token: string) => api.post<InvitationInfo>("/auth/invitations/lookup", { json: { token } });
export const acceptInvitation = (token: string, username: string, password: string, confirm: string, acceptTerms?: string) =>
  api.post<User>("/auth/accept-invite", { json: { token, username, password, confirm_password: confirm, accept_terms: acceptTerms ?? null } });
/** Agree to the current terms (the updated-terms screen). */
export const acceptTerms = (version: string) => api.post<User>("/auth/accept-terms", { json: { version } });
